<template>
  <div>
    <v-card class="pa-5 mb-5 elevation-hb">
      <v-row class="mb" align="center" justify="center">
        <v-col>
          <h3>Consolidação Mensal</h3>
        </v-col>
        <v-col class="text-right">
          <v-chip v-if="consolidated" label color="green" dark>Consolidado</v-chip>
          <v-chip v-if="!consolidated && date" label @click="consolidate()">Consolidar</v-chip>

          <v-chip v-if="!consolidated && date" color="primary" label @click="sync()">Sincronizar</v-chip>
        </v-col>
        <v-col cols="3">
          <v-menu
            ref="menu"
            v-model="menu"
            :close-on-content-click="true"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="date"
                label="Mês de Referência"
                prepend-icon="mdi-calendar"
                readonly
                outlined
                dense
                hide-details
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker v-model="date" type="month" no-title scrollable @change="loadTotals" locale="pt-br">
            </v-date-picker>
          </v-menu>
        </v-col>
        <v-col class="text-right" cols="2">
          <v-btn
            depressed 
            color="primary" 
            class="text-center mr-1" 
            :disabled="!totals.length"
            :dark="totals.length" 
            @click="downloadCSV()" 
            block
          >
            <v-icon small class="mx-2">mdi-magnify</v-icon>
            Exportar CSV
          </v-btn>
        </v-col>
      </v-row>

      <v-divider class="my-3"></v-divider>

      <v-data-table
        :items="totals"
        :headers="headers"
        :loading="loading"
        show-expand
        single-expand
        :items-per-page="100"
        item-key="sellerId"
        locale="pt-BR"
      >
        <template v-slot:[`item.amountPaid`]="{ item }">
          {{ item.amountPaid | formatFullMoney }}
        </template>
        <template v-slot:[`item.amountTotal`]="{ item }">
          {{ item.amountTotal | formatFullMoney }}
        </template>
        <template v-slot:[`item.amountBruto`]="{ item }">
          {{ (item.amountFees + item.amountLiquid + item.amountRefunds) | formatFullMoney }}
        </template>
        <template v-slot:[`item.amountNFE`]="{ item }">
          [
          {{ ((item.amountTotal - item.amountShipping) * 0.1) | formatFullMoney }}
          ]
        </template>
        <template v-slot:[`item.ammountBonus`]="{ item }">
          [
          {{
            (item.amountFees + item.amountLiquid + item.amountRefunds - (item.amountTotal - item.amountShipping) * 0.1)
              | formatFullMoney
          }}
          ]
        </template>
        <template v-slot:[`item.amountOtherReceivers`]="{ item }">
          {{ item.amountOtherReceivers | formatFullMoney }}
        </template>
        <template v-slot:[`item.amountShipping`]="{ item }">
          {{ item.amountShipping | formatFullMoney }}
        </template>
        <template v-slot:[`item.consolidatedAt`]="{ item }">
          {{ item.consolidatedAt | formatDate }}
        </template>
        <template v-slot:[`item.action`]="{ item }">
          <v-tooltip left>
            <template v-slot:activator="{ on }" color="amber">
              <v-btn icon @click="uploadDialog(item)" v-on="on">
                <v-icon>mdi-upload</v-icon>
              </v-btn>
            </template>
            <span>Enviar Nota Fiscal</span>
          </v-tooltip>
          <v-tooltip left v-if="item.invoicePath">
            <template v-slot:activator="{ on }" color="amber">
              <v-btn icon color="green" @click="getInvoice(item)" v-on="on">
                <v-icon>mdi-download</v-icon>
              </v-btn>
            </template>
            <span>Download Nota Fiscal</span>
          </v-tooltip>
        </template>
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length">
            <v-simple-table class="my-5">
              <tr>
                <td><b>Seller</b></td>
                <td width="80%">{{ item.sellerId }} ({{ item.sellerCnpj }} - {{ item.socialName }})</td>
              </tr>
              <tr>
                <td><b>Valor para Emissão da Nota Fiscal</b></td>
                <td>
                  {{ (item.amountFees + item.amountLiquid + item.amountRefunds) | formatFullMoney }}
                </td>
              </tr>
              <tr>
                <td><b>Mês de Referência</b></td>
                <td>{{ toMonth(date) | capitalize }}</td>
              </tr>
              <tr>
                <td><b>Descrição</b></td>
                <td>
                  Comissão sobre as vendas no Marketplace Wap, referente a
                  {{ toMonth(date) }}.
                </td>
              </tr>
            </v-simple-table>

            <div class="pa-1">
              <v-data-table
                :items="item.orders"
                :headers="headersOrders"
                :loading="loading"
                :items-per-page="100"
                dense
                class="display"
                locale="pt-BR"
              >
                <template v-slot:[`item.amountPaid`]="{ item }">
                  {{ item.amountPaid | formatFullMoney }}
                </template>
                <template v-slot:[`item.clientFullName`]="{ item }">
                  {{ item.clientFirstName }} {{ item.clientLastName }}
                </template>
                <template v-slot:[`item.amountTotal`]="{ item }">
                  {{ item.amountTotal | formatFullMoney }}
                </template>
                <template v-slot:[`item.amountFees`]="{ item }">
                  {{ item.amountFees | formatFullMoney }}
                </template>
                <template v-slot:[`item.amountRefunds`]="{ item }">
                  {{ item.amountRefunds | formatFullMoney }}
                </template>
                <template v-slot:[`item.amountLiquid`]="{ item }">
                  {{ item.amountLiquid | formatFullMoney }}
                </template>
                <template v-slot:[`item.amountOtherReceivers`]="{ item }">
                  {{ item.amountOtherReceivers | formatFullMoney }}
                </template>
                <template v-slot:[`item.amountShipping`]="{ item }">
                  {{ item.amountShipping | formatFullMoney }}
                </template>
                <template v-slot:[`item.createdAt`]="{ item }">
                  {{ item.createdAt | formatFullDate }}
                </template>
                <template v-slot:[`item.updatedAt`]="{ item }">
                  {{ item.updatedAt | formatFullDate }}
                </template>
              </v-data-table>
            </div>
          </td>
        </template>
      </v-data-table>
    </v-card>

    <v-snackbar v-model="snackbar">
      {{ text }}
      <template v-slot:action="{ attrs }">
        <v-btn color="pink" text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <TheDialog icon="mdi-cash-register" title="Salvar Nota Fiscal" v-model="dialog">
      <template>
        <v-container>
          <v-form ref="form">
            <b> Nota Fiscal </b>
            <v-file-input
              label="Escolha o Arquivo"
              placeholder="Selecione..."
              id="file"
              color="primary"
              class="my-1"
              outlined
            ></v-file-input>
            <b> Número </b>
            <v-text-field
              placeholder="Número da Nota Fiscal"
              outlined
              v-model="orderNumber"
              :error="error"
            ></v-text-field>
          </v-form>
        </v-container>
      </template>
      <template v-slot:actions>
        <div class="flex-grow-1"></div>
        <v-btn color="primary text--white" depressed @click="saveInvoice()">Salvar</v-btn>
      </template>
    </TheDialog>
  </div>
</template>

<script>
import axios from 'axios'
import { baseApiUrl, showError } from '@/global'
import TheDialog from '@/components/TheDialog'

export default {
  name: 'WireCard',
  components: { TheDialog },
  data: () => ({
    date: null,
    consolidated: false,
    menu: null,
    picker: null,
    loading: false,
    snackbar: false,
    headers: [
      { text: 'Seller Id', value: 'sellerId' },
      { text: 'Quantidade Pedidos', value: 'amountOrders' },
      { text: 'Valor Total', value: 'amountTotal' },
      { text: 'Valor Frete', value: 'amountShipping' },
      { text: 'Valor Seller ', value: 'amountOtherReceivers' },
      { text: 'Comissão Wap', value: 'amountBruto' },
      { text: 'Valor Nota', value: 'amountNFE' },
      { text: 'Diferença Comição/Nota', value: 'ammountBonus' },
      { text: 'Consolidado em', value: 'consolidatedAt' },
      { text: 'Número da Nota', value: 'orderNumber' },
      { text: 'Ações', value: 'action' },
    ],
    headersOrders: [
      { text: 'Pedido', value: 'orderId' },
      { text: 'Cliente', value: 'clientFullName' },
      { text: 'Valor Pago', value: 'amountPaid' },
      { text: 'Valor Total', value: 'amountTotal' },
      { text: 'Taxa', value: 'amountFees' },
      { text: 'Refundos', value: 'amountRefunds' },
      { text: 'Valor Wap', value: 'amountLiquid' },
      { text: 'Valor Seller ', value: 'amountOtherReceivers' },
      { text: 'Frete', value: 'amountShipping' },
      { text: 'Criado em', value: 'createdAt' },
      { text: 'Ultima Atualização', value: 'updatedAt' },
    ],
    headersTotal: [],
    orders: [],
    totals: [],
    dialog: false,
    select: {},
    text: '',
    orderNumber: null,
    error: false,
  }),
  methods: {
    toMonth(date) {
      const today = new Date(date)
      return today.toLocaleString('pt-BR', { month: 'long', timeZone: 'UTC' }) + '/' + today.getFullYear()
    },
    loadTotals() {
      const url = `${baseApiUrl}/finantial/totalConsolidated`
      this.loading = true
      const d = new Date()
      let date = this.formatDate(d)
      if (this.date) {
        date = this.date
      }
      axios
        .get(url, { params: { date } })
        .then((res) => {
          this.totals = res.data.totals
          this.consolidated = res.data.consolidated
          this.loading = false
        })
        .catch(showError)
    },
    formatDate(date) {
      var d = new Date(date),
        month = '' + (d.getMonth() + 1),
        day = '' + d.getDate(),
        year = d.getFullYear()
      if (month.length < 2) month = '0' + month
      if (day.length < 2) day = '0' + day
      return [year, month].join('-')
    },
    uploadDialog(item) {
      this.select = item
      this.dialog = true
    },
    saveInvoice() {
      this.error = false
      if (!this.orderNumber) {
        this.error = true
        return false
      }
      this.loading = true
      this.dialog = false
      const url = `${baseApiUrl}/finantial/invoice`
      const file = document.querySelector('#file')

      var formData = new FormData()
      formData.append('file', file.files[0])
      formData.append('sellerId', this.select.sellerId)
      formData.append('month', this.select.month)
      formData.append('year', this.select.year)
      formData.append('number', this.orderNumber)

      axios
        .post(url, formData, {
          headers: {
            'Content-Type': `multipart/form-data; boundary=${formData._boundary}`,
          },
        })
        .then((res) => {
          this.loading = false
          this.loadTotals()
        })
        .catch((err) => {
          this.snackColor = 'error'
          this.snackText = err.message
        })
    },
    getInvoice(arquivo) {
      const url = `${baseApiUrl}/finantial/invoice`
      this.loading = true
      const d = new Date()
      let date = this.formatDate(d)
      if (this.date) {
        date = this.date
      }
      axios
        .get(url, {
          params: {
            sellerId: arquivo.sellerId,
            month: arquivo.month,
            year: arquivo.year,
          },
          responseType: 'blob',
        })
        .then((res) => {
          this.loading = false
          const url = window.URL.createObjectURL(new Blob([res.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', arquivo.invoicePath)
          document.body.appendChild(link)
          link.click()
        })
        .catch((err) => {
          console.log(err)
        })
      // .catch(showError)
    },
    consolidate() {
      const url = `${baseApiUrl}/orders/consolidate`
      if (!this.date) {
        return
      }
      this.loading = true
      axios
        .post(url, { date: this.date })
        .then((res) => {
          this.loadTotals()
        })
        .catch(showError)
    },
    sync() {
      const url = `${baseApiUrl}/orders/sync`
      this.loading = true
      axios
        .post(url, { date: this.date })
        .then((res) => {
          this.text = res.data.info
          this.loading = false
          this.snackbar = true
        })
        .catch(showError)
    },
    downloadCSV() {
      const data = this.totals
      const fileName = 'download-consolidacao-mensal'
      const csv = this.convertArrayOfObjectsToCSV(data)
      const link = document.createElement('a')
      link.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv)
      link.download = fileName + '.csv'
      link.click()
    },
    convertArrayOfObjectsToCSV(data) {
      const columnDelimiter = ','
      const lineDelimiter = '\n'
      const keys = Object.keys(data[0])

      let result = ''
      result += keys.join(columnDelimiter)
      result += lineDelimiter

      data.forEach((item) => {
        let ctr = 0
        keys.forEach((key) => {
          if (ctr > 0) result += columnDelimiter
          result += item[key]
          ctr++
        })
        result += lineDelimiter
      })

      return result
    },
  },
  mounted() {
    this.loadTotals()
  },
}
</script>

<style lang="scss" scoped></style>
